import { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserCog, Plus, Search, Mail, Trash2, Shield, ClipboardList, Link2, Copy, CheckCircle, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;

const ClubManagers = () => {
  const { club } = useOutletContext();
  const { clubId } = useParams();
  const [managers, setManagers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showInviteLinkModal, setShowInviteLinkModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [generatedInviteLink, setGeneratedInviteLink] = useState('');
  const [inviteForm, setInviteForm] = useState({
    parent_email: '',
    parent_name: '',
    team_id: ''
  });
  const [editForm, setEditForm] = useState({
    team_id: '',
    status: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('club_token');
    try {
      const [managersRes, teamsRes] = await Promise.all([
        axios.get(`${API}/clubs/managers`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/clubs/teams`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      
      // Transform managers data from club-wide format to team-assignment format
      const transformedManagers = [];
      (managersRes.data || []).forEach(manager => {
        (manager.teams || []).forEach(teamInfo => {
          transformedManagers.push({
            id: `${manager.id}_${teamInfo.team_id}`,
            manager_id: manager.manager_id || manager.id,
            manager_name: manager.full_name,
            manager_email: manager.email,
            phone: manager.phone,
            status: manager.status,
            team_id: teamInfo.team_id,
            team_name: teamInfo.team_name,
            role: teamInfo.role,
            assigned_at: teamInfo.assigned_at
          });
        });
      });
      
      setManagers(transformedManagers);
      setTeams(teamsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
    setLoading(false);
  };

  const generateInviteLink = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    const inviteCode = btoa(JSON.stringify({
      clubId,
      teamId,
      clubName: club?.name,
      teamName: team?.name,
      type: 'manager',
      created: Date.now()
    }));
    return `${APP_URL}/team/join/${inviteCode}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard!');
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.team_id) {
      toast.error('Please select a team');
      return;
    }
    
    // Generate invite link
    const inviteLink = generateInviteLink(inviteForm.team_id);
    
    const token = localStorage.getItem('club_token');
    try {
      await axios.post(`${API}/clubs/teams/${inviteForm.team_id}/managers`, {
        parent_email: inviteForm.parent_email,
        parent_name: inviteForm.parent_name || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Manager added successfully!');
      setShowInviteModal(false);
      // Show invite link modal
      setGeneratedInviteLink(inviteLink);
      setShowInviteLinkModal(true);
      setInviteForm({ parent_email: '', parent_name: '', team_id: '' });
      fetchData();
    } catch (error) {
      // Even if email fails, show the invite link
      setGeneratedInviteLink(inviteLink);
      setShowInviteModal(false);
      setShowInviteLinkModal(true);
      toast.info('Email may not have been sent. Use the invite link below.');
    }
  };

  const handleGenerateLinkOnly = () => {
    if (!inviteForm.team_id) {
      toast.error('Please select a team');
      return;
    }
    const inviteLink = generateInviteLink(inviteForm.team_id);
    setGeneratedInviteLink(inviteLink);
    setShowInviteModal(false);
    setShowInviteLinkModal(true);
  };

  const handleRemove = async (teamId, managerId) => {
    if (!confirm('Are you sure you want to remove this manager?')) return;
    const token = localStorage.getItem('club_token');
    try {
      await axios.delete(`${API}/clubs/teams/${teamId}/managers/${managerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Manager removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove manager');
    }
  };

  const handleEdit = (manager) => {
    setSelectedManager(manager);
    setEditForm({
      team_id: manager.team_id,
      status: manager.status || 'active'
    });
    setShowEditModal(true);
  };

  const handleUpdateManager = async (e) => {
    e.preventDefault();
    if (!selectedManager) return;
    
    const token = localStorage.getItem('club_token');
    try {
      await axios.put(`${API}/clubs/managers/${selectedManager.manager_id}/assignment`, {
        team_id: editForm.team_id !== selectedManager.team_id ? editForm.team_id : undefined,
        status: editForm.status !== selectedManager.status ? editForm.status : undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Manager updated successfully');
      setShowEditModal(false);
      setSelectedManager(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update manager');
    }
  };

  const filteredManagers = managers.filter(manager =>
    manager.manager_name?.toLowerCase().includes(search.toLowerCase()) ||
    manager.manager_email?.toLowerCase().includes(search.toLowerCase()) ||
    manager.team_name?.toLowerCase().includes(search.toLowerCase())
  );

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team?.name || 'Unknown Team';
  };

  return (
    <div className="space-y-6" data-testid="club-managers">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl uppercase">
            Team <span className="text-secondary">Managers</span>
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Parents who help with team logistics
          </p>
        </div>
        <Button 
          onClick={() => setShowInviteModal(true)}
          className="bg-secondary text-black hover:bg-secondary/90"
          disabled={teams.length === 0}
        >
          <Plus size={18} className="mr-2" />
          Add Manager
        </Button>
      </div>

      {teams.length === 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-4">
          <p className="text-sm text-yellow-400">
            Create a team first before adding managers.
          </p>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search managers..."
          className="bg-transparent border-border pl-10"
        />
      </div>

      {/* Managers List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-secondary border-t-transparent rounded-full mx-auto"></div>
        </div>
      ) : filteredManagers.length === 0 ? (
        <div className="text-center py-12 bg-card border border-border/40 rounded-sm">
          <UserCog className="mx-auto text-muted-foreground mb-4" size={48} />
          <p className="text-muted-foreground mb-4">
            {search ? 'No managers found' : 'No team managers assigned yet'}
          </p>
          {!search && teams.length > 0 && (
            <Button onClick={() => setShowInviteModal(true)} className="bg-secondary text-black hover:bg-secondary/90">
              <Plus size={18} className="mr-2" />
              Add Your First Manager
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-card border border-border/40 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-card/50 border-b border-border/40">
              <tr>
                <th className="text-left text-xs uppercase tracking-widest text-muted-foreground p-4">Manager</th>
                <th className="text-left text-xs uppercase tracking-widest text-muted-foreground p-4">Team</th>
                <th className="text-left text-xs uppercase tracking-widest text-muted-foreground p-4">Status</th>
                <th className="text-left text-xs uppercase tracking-widest text-muted-foreground p-4">Assigned</th>
                <th className="text-right text-xs uppercase tracking-widest text-muted-foreground p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredManagers.map((manager, idx) => (
                <motion.tr
                  key={manager.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: idx * 0.05 }}
                  className="border-b border-border/20 hover:bg-card/50"
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        manager.status === 'active' ? 'bg-secondary/20' : 'bg-amber-500/20'
                      }`}>
                        <UserCog className={manager.status === 'active' ? 'text-secondary' : 'text-amber-400'} size={18} />
                      </div>
                      <div>
                        <p className="font-medium">{manager.manager_name}</p>
                        <p className="text-xs text-muted-foreground">{manager.manager_email}</p>
                        {manager.status === 'pending' && (
                          <span className="text-xs text-amber-400">Pending Registration</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm">{manager.team_name}</span>
                  </td>
                  <td className="p-4">
                    <span className={`text-xs px-2 py-1 rounded-sm ${
                      manager.status === 'active' 
                        ? 'bg-emerald-500/20 text-emerald-400' 
                        : 'bg-amber-500/20 text-amber-400'
                    }`}>
                      {manager.status === 'active' ? 'Active' : 'Pending'}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    {manager.assigned_at ? new Date(manager.assigned_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(manager)}
                        className="text-muted-foreground hover:text-secondary"
                        data-testid={`edit-manager-${manager.manager_id}`}
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(manager.team_id, manager.manager_id)}
                        className="text-muted-foreground hover:text-red-400"
                        data-testid={`remove-manager-${manager.manager_id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Manager Permissions Info */}
      <div className="bg-card border border-border/40 rounded-sm p-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <ClipboardList className="text-secondary" size={18} />
          Manager Permissions (Logistics Only)
        </h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-emerald-400 font-medium mb-1">Managers CAN:</p>
            <ul className="space-y-1 text-muted-foreground">
              <li>• Edit session details</li>
              <li>• Add matches & tournaments</li>
              <li>• Update schedules</li>
              <li>• Send logistical notes</li>
              <li>• Manage attendance</li>
            </ul>
          </div>
          <div>
            <p className="text-red-400 font-medium mb-1">Managers CANNOT:</p>
            <ul className="space-y-1 text-muted-foreground">
              <li>• Modify training plans</li>
              <li>• Access technical tools</li>
              <li>• Access drills library</li>
              <li>• Access financial data</li>
              <li>• Remove coaches</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowInviteModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border/40 rounded-sm w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-heading text-xl uppercase mb-6">Add Team Manager</h2>
            
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Parent Name
                </label>
                <Input
                  type="text"
                  value={inviteForm.parent_name}
                  onChange={(e) => setInviteForm({...inviteForm, parent_name: e.target.value})}
                  className="bg-transparent border-border focus:border-secondary"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Parent Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                  <Input
                    type="email"
                    value={inviteForm.parent_email}
                    onChange={(e) => setInviteForm({...inviteForm, parent_email: e.target.value})}
                    required
                    className="bg-transparent border-border pl-10 focus:border-secondary"
                    placeholder="parent@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Assign to Team *
                </label>
                <select
                  value={inviteForm.team_id}
                  onChange={(e) => setInviteForm({...inviteForm, team_id: e.target.value})}
                  required
                  className="w-full bg-transparent border border-border rounded-sm p-3 text-white focus:border-secondary"
                >
                  <option value="" className="bg-card">Select a team</option>
                  {teams.filter(t => t.status === 'active').map(team => (
                    <option key={team.id} value={team.id} className="bg-card">{team.name}</option>
                  ))}
                </select>
              </div>

              <div className="bg-secondary/10 border border-secondary/30 rounded-sm p-3">
                <p className="text-xs text-secondary">
                  Managers are typically parents who help with team logistics — not technical coaching.
                </p>
              </div>

              <div className="flex flex-col gap-3 pt-4">
                <Button 
                  type="submit"
                  className="w-full bg-secondary text-black hover:bg-secondary/90"
                >
                  <Mail size={16} className="mr-2" />
                  Send Email Invite
                </Button>
                <Button 
                  type="button"
                  variant="outline"
                  onClick={handleGenerateLinkOnly}
                  className="w-full border-secondary/50 text-secondary hover:bg-secondary/10"
                >
                  <Link2 size={16} className="mr-2" />
                  Generate Invite Link Only
                </Button>
                <Button 
                  type="button" 
                  variant="ghost" 
                  onClick={() => setShowInviteModal(false)}
                  className="w-full text-muted-foreground"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Invite Link Modal */}
      {showInviteLinkModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowInviteLinkModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border/40 rounded-sm w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-emerald-400" size={32} />
              </div>
              <h2 className="font-heading text-xl uppercase">Invite Link Generated</h2>
              <p className="text-sm text-muted-foreground mt-2">
                Share this link with the manager to join your team
              </p>
            </div>
            
            <div className="bg-card/50 border border-border/40 rounded-sm p-4 mb-4">
              <p className="text-xs text-muted-foreground uppercase mb-2">Invite Link</p>
              <div className="flex gap-2">
                <Input
                  value={generatedInviteLink}
                  readOnly
                  className="bg-transparent border-border text-xs flex-1"
                />
                <Button
                  onClick={() => copyToClipboard(generatedInviteLink)}
                  className="bg-secondary text-black hover:bg-secondary/90"
                >
                  <Copy size={16} />
                </Button>
              </div>
            </div>

            <div className="bg-secondary/10 border border-secondary/30 rounded-sm p-3 mb-4">
              <p className="text-xs text-secondary">
                The manager will be able to access the team workspace after clicking this link.
              </p>
            </div>

            <Button 
              onClick={() => setShowInviteLinkModal(false)}
              className="w-full bg-emerald-500 text-black hover:bg-emerald-400"
            >
              Done
            </Button>
          </motion.div>
        </div>
      )}

      {/* Edit Manager Modal */}
      {showEditModal && selectedManager && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowEditModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border/40 rounded-sm w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="edit-manager-modal"
          >
            <h2 className="font-heading text-xl uppercase mb-6">Edit Manager Assignment</h2>
            
            <div className="mb-4 p-3 bg-card/50 border border-border/40 rounded-sm">
              <p className="text-sm font-medium">{selectedManager.manager_name}</p>
              <p className="text-xs text-muted-foreground">{selectedManager.manager_email}</p>
            </div>
            
            <form onSubmit={handleUpdateManager} className="space-y-4">
              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Assigned Team
                </label>
                <select
                  value={editForm.team_id}
                  onChange={(e) => setEditForm({...editForm, team_id: e.target.value})}
                  className="w-full bg-transparent border border-border rounded-sm p-3 text-white focus:border-secondary"
                  data-testid="edit-manager-team-select"
                >
                  {teams.filter(t => t.status === 'active').map(team => (
                    <option key={team.id} value={team.id} className="bg-card">{team.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Status
                </label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                  className="w-full bg-transparent border border-border rounded-sm p-3 text-white focus:border-secondary"
                  data-testid="edit-manager-status-select"
                >
                  <option value="active" className="bg-card">Active</option>
                  <option value="pending" className="bg-card">Pending</option>
                  <option value="inactive" className="bg-card">Inactive</option>
                </select>
              </div>

              <div className="bg-secondary/10 border border-secondary/30 rounded-sm p-3">
                <p className="text-xs text-secondary">
                  Managers help with team logistics. They cannot access technical coaching tools.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="ghost" 
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 text-muted-foreground"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  className="flex-1 bg-secondary text-black hover:bg-secondary/90"
                  data-testid="save-manager-changes-btn"
                >
                  Save Changes
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ClubManagers;
